// Undo/Redo V1 (Feb 2026) -- tiny in-memory undo/redo stack for the
// visible grid on Roster and Rota Style. Frontend state only: history
// exists while the page is mounted and is wiped by refresh, logout,
// navigation and any week / team / profile change (callers clear()).
// Capped at `limit` steps. No persistence, no backend awareness.
import { useCallback, useState } from "react";

export function useGridUndo(limit = 20) {
  const [stacks, setStacks] = useState({ undo: [], redo: [] });

  // A new edit always clears redo (standard undo semantics).
  const push = useCallback((record) => {
    setStacks((prev) => ({
      undo: [...prev.undo.slice(-(limit - 1)), record],
      redo: [],
    }));
  }, [limit]);

  // Called ONLY after the undo write succeeded: moves the top undo
  // record onto the redo stack. On failure the caller simply does not
  // commit, so the step stays available.
  const commitUndo = useCallback(() => {
    setStacks((prev) => {
      if (!prev.undo.length) return prev;
      const rec = prev.undo[prev.undo.length - 1];
      return {
        undo: prev.undo.slice(0, -1),
        redo: [...prev.redo.slice(-(limit - 1)), rec],
      };
    });
  }, [limit]);

  const commitRedo = useCallback(() => {
    setStacks((prev) => {
      if (!prev.redo.length) return prev;
      const rec = prev.redo[prev.redo.length - 1];
      return {
        undo: [...prev.undo.slice(-(limit - 1)), rec],
        redo: prev.redo.slice(0, -1),
      };
    });
  }, [limit]);

  const clear = useCallback(() => setStacks({ undo: [], redo: [] }), []);

  return {
    canUndo: stacks.undo.length > 0,
    canRedo: stacks.redo.length > 0,
    peekUndo: () => stacks.undo[stacks.undo.length - 1] || null,
    peekRedo: () => stacks.redo[stacks.redo.length - 1] || null,
    push,
    commitUndo,
    commitRedo,
    clear,
  };
}
